import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  HiAcademicCap as Academic,
  HiPlus as Add,
  HiHome as Building,
  HiClock as Clock,
  HiTrash as Delete,
  HiPencil as Edit,
  HiClipboardList as List,
  HiUpload as Upload,
  HiUser as User,
  HiUsers as Users,
  HiEye as Visibility,
} from 'react-icons/hi';
import { mockData } from './MockData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`upload-tabpanel-${index}`}
      aria-labelledby={`upload-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UploadDataManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [_showAddDialog, setShowAddDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [_selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [staff] = useState(mockData.staff);
  const [students] = useState(mockData.students);
  const [subjects] = useState(mockData.subjects);
  const [rooms] = useState(mockData.rooms);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);

    if (files.length > 0) {
      setIsUploading(true);
      setUploadProgress(0);

      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsUploading(false);
            setShowUploadDialog(false);
            return 100;
          }
          return prev + Math.random() * 20;
        });
      }, 500);
    }
  };

  const _getEntityIcon = (type: string) => {
    switch (type) {
      case 'staff':
        return <User />;
      case 'students':
        return <Academic />;
      case 'subjects':
        return <List />;
      case 'rooms':
        return <Building />;
      case 'classes':
        return <Users />;
      case 'periods':
        return <Clock />;
      default:
        return <List />;
    }
  };

  const renderDataTable = (data: any[], columns: string[], _type: string) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column}>{column}</TableCell>
            ))}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.slice(0, 10).map((item, index) => (
            <TableRow key={index}>
              {columns.map((column) => (
                <TableCell key={column}>
                  {typeof item[column.toLowerCase().replace(' ', '')] === 'boolean'
                    ? item[column.toLowerCase().replace(' ', '')]
                      ? 'Yes'
                      : 'No'
                    : item[column.toLowerCase().replace(' ', '')] || 'N/A'}
                </TableCell>
              ))}
              <TableCell>
                <IconButton size="small" color="primary">
                  <Visibility />
                </IconButton>
                <IconButton size="small" color="primary">
                  <Edit />
                </IconButton>
                <IconButton size="small" color="error">
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderEntityTab = (entityType: string, data: any[], columns: string[]) => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{entityType.charAt(0).toUpperCase() + entityType.slice(1)} Management</Typography>
          <Box>
            <Button startIcon={<Upload />} onClick={() => setShowUploadDialog(true)} sx={{ mr: 1 }}>
              Upload CSV
            </Button>
            <Button startIcon={<Add />} onClick={() => setShowAddDialog(true)}>
              Add {entityType.slice(0, -1)}
            </Button>
          </Box>
        </Box>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
                  <Typography variant="h4" color="primary">
                    {data.length}
                  </Typography>
                  <Typography variant="body2">Total {entityType}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                  <Typography variant="h4" color="success.main">
                    {Math.floor(data.length * 0.85)}
                  </Typography>
                  <Typography variant="body2">Active</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                  <Typography variant="h4" color="warning.main">
                    {Math.floor(data.length * 0.1)}
                  </Typography>
                  <Typography variant="body2">Pending</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                  <Typography variant="h4" color="error.main">
                    {Math.floor(data.length * 0.05)}
                  </Typography>
                  <Typography variant="body2">Issues</Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {renderDataTable(data, columns, entityType)}
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Upload Data Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Import, manage, and validate essential data for the timetabling system
      </Typography>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="upload data tabs">
            <Tab icon={<User />} label="Staff" />
            <Tab icon={<Academic />} label="Students" />
            <Tab icon={<List />} label="Subjects" />
            <Tab icon={<Building />} label="Rooms" />
            <Tab icon={<Users />} label="Classes" />
            <Tab icon={<Clock />} label="Time Periods" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderEntityTab('staff', staff, ['Name', 'Email', 'Department', 'Role', 'Status'])}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderEntityTab('students', students, ['Name', 'Student ID', 'Year Group', 'Class', 'Status'])}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {renderEntityTab('subjects', subjects, ['Name', 'Code', 'Department', 'Level', 'Status'])}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {renderEntityTab('rooms', rooms, ['Name', 'Type', 'Capacity', 'Building', 'Equipment'])}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            {renderEntityTab('classes', [], ['Name', 'Year Group', 'Size', 'Form Tutor', 'Room'])}
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            {renderEntityTab('periods', [], ['Name', 'Start Time', 'End Time', 'Duration', 'Type'])}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onClose={() => setShowUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload CSV File</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please ensure your CSV file follows the correct format for the selected data type.
            </Alert>

            <input type="file" accept=".csv" onChange={handleFileUpload} style={{ marginBottom: '16px' }} />

            {isUploading && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Uploading... {Math.round(uploadProgress)}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={isUploading}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadDataManagement;
